import type { PublicRuntimeConfig } from '@nuxt/schema';
import type { NitroFetchOptions } from 'nitropack';
import { useEnvironment } from '~/composables/useStates/useEnvironment';
import { globalKeys } from 'assets/data/global-keys';
import { useStorageService } from '~/composables/services/useStorageService';
import { useSlugify } from '~/composables/useFormatters';
import useGetPage from '~/composables/useGetPage';
import useLocaleUtils from '~/composables/useLocaleUtils';
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import { useCachebust } from '~/composables/useStates/useCachebust';
import type { TCacheBustKeys } from '~/interfaces/states/cachebust.state';

const apiCallCache = shallowRef(
  new Map<
    string,
    { cacheHitCount: number; optionsUseCacheEnabled: boolean[] }
  >(),
);

export interface IApiRequest extends NitroFetchOptions<string & {}> {
  base?: keyof PublicRuntimeConfig;
  enabled?: boolean;
  useAuthentication?: boolean;
  usePlayerId?: boolean;
  useBrandId?: boolean;
  useChannel?: boolean;
  useRegionCode?: boolean;
  useCountryCode?: boolean;
  useVertical?: boolean;
  useEnvironment?: boolean;
  useLanguageCode?: boolean;
  useOrigin?: boolean;
  useCurrency?: boolean;
  isContent?: boolean;
  cacheKey?: string;
  useCache?: boolean;
  cacheBustKey?: TCacheBustKeys;
}

type TEndpointsWithNoTimeout =
  | 'authApi'
  | 'balance'
  | 'player'
  | 'rewards'
  | 'casinoBonus'
  | 'info';

export default defineNuxtPlugin({
  name: 'api',
  parallel: true,
  setup() {
    const $storageService = useStorageService();

    const abortController = new AbortController();
    const { public: publicConfig } = useRuntimeConfig();
    const api = <T>(url: string, options: IApiRequest = { enabled: true }) => {
      const authStore = useAuthStore();
      const siteStore = useSiteStore();
      const { getCacheBust, setCacheBust } = useCachebust();
      const { getLocale } = useLocaleUtils();
      const environment = useEnvironment();
      const page = useGetPage({ setSeoMeta: false });
      // setup
      const baseURL = String(publicConfig[options.base]);
      url = url.startsWith('http')
        ? url
        : !!baseURL
          ? `${baseURL}/${url}`
          : url;
      // headers
      const headers = options.headers || {};
      if (options.useAuthentication)
        headers['Authorization'] =
          `Bearer ${authStore.access_token || authStore.currentAccessToken || useCookie(globalKeys.authKeys.accessToken)?.value}`;
      if (options.usePlayerId) headers['x-playerId'] = authStore.user_id;
      if (options.useBrandId)
        headers['X-Brand-Id'] = environment.value.regionConfigs.brandId;
      if (options.useRegionCode)
        headers['X-Region-Code'] = environment.value.regionConfigs.regionCode;
      if (options.useVertical) headers['X-Vertical'] = page.value?.Mapping;
      if (options.useEnvironment)
        headers['X-Environment'] = environment.value.environment.variable;
      if (options.useOrigin) headers['X-OT-Origin'] = window.location.origin;

      if (
        (!options.method || options.method.toLowerCase() === 'get') &&
        !headers['Content-Type']
      )
        headers['Content-Type'] = 'text/plain';

      const query = options.query || {};
      if (options.useRegionCode) {
        query['regionCode'] = environment.value.regionConfigs.regionCode;
        query['region'] = environment.value.regionConfigs.regionCode;
        url = url.replaceAll(
          `{region}`,
          environment.value.regionConfigs.regionCode,
        );
      }
      if (options.useCountryCode) {
        query['countryCode'] = environment.value.regionConfigs.countryCode;
        query['country'] = environment.value.regionConfigs.countryCode;
        url = url.replaceAll(
          `{countryCode}`,
          environment.value.regionConfigs.countryCode,
        );
      }
      if (options.useVertical)
        query['vertical'] = page.value?.Mapping || 'home';
      if (options.useEnvironment)
        query['environment'] = environment.value.environment.variable;
      if (options.useChannel) {
        query['channel'] = environment.value.deviceConfig.channel;
        url = url.replaceAll(
          `{channel}`,
          environment.value.deviceConfig.channel,
        );
      }
      if (options.useLanguageCode) {
        if (options.isContent) query['lang'] = getLocale().split('-')[0];
        else {
          query['languageCode'] = getLocale();
          url = url.replaceAll(`{languageCode}`, getLocale());
        }
      }
      if (options.useCurrency) query['currency'] = siteStore.getCurrencyCode;
      if (
        !!options.cacheBustKey &&
        (options.method?.toUpperCase() === 'GET' || !options.method)
      ) {
        const cachebust = getCacheBust(options.cacheBustKey);
        if (!!cachebust) query['cachebust'] = cachebust;
      }

      // cache key
      const cacheKey =
        'cache:' +
        useSlugify(
          `${url}${!!query ? '-' + Object.entries(query).join() : ''}${!!options.cacheKey ? '-' + options.cacheKey : ''}`,
        );

      // Duplicate api call checks.
      const apiCacheDisabled = $storageService.getSessionStorage({
        key: globalKeys.developerConfigKeys.debugDisableCache,
      });
      if (apiCacheDisabled) {
        if (apiCallCache.value.has(cacheKey)) {
          apiCallCache.value.set(cacheKey, {
            cacheHitCount:
              apiCallCache.value.get(cacheKey)?.cacheHitCount + 1 || 1,
            optionsUseCacheEnabled: [
              ...apiCallCache.value.get(cacheKey)?.optionsUseCacheEnabled,
              options.useCache ?? false,
            ],
          });
        } else
          apiCallCache.value.set(cacheKey, {
            cacheHitCount: 1,
            optionsUseCacheEnabled: [options.useCache ?? false],
          });

        console.log(
          `%c ************** cache hit for ${cacheKey} **************`,
          'background: #222; color: #bada55',
        );
        console.table(Object.fromEntries(apiCallCache.value));
        options.useCache = false;
      }

      // interceptors
      const onResponseError = [
        ({ request, response, options }) => {
          // Log error
          if (import.meta.env.ENABLE_LOGGING)
            console.log(
              '[fetch response error]',
              request,
              response.status,
              response.body,
              options,
            );
        },
      ];

      if (options.onResponseError) {
        (Array.isArray(options.onResponseError)
          ? options.onResponseError
          : [options.onResponseError]
        ).forEach((h) => onResponseError.push(h));
      }

      const onResponse = [
        async ({ request, response }) => {
          // Log response
          if (import.meta.env.ENABLE_LOGGING)
            console.log(
              '[fetch response]',
              request,
              response.status,
              response.body,
            );
          if (
            options.cacheBustKey &&
            !!options.method &&
            options.method?.toUpperCase() !== 'GET'
          ) {
            setCacheBust(options.cacheBustKey);
          }
          if (
            !!options?.useCache &&
            !response?.fromCache &&
            !!navigator?.storage?.estimate
          ) {
            const estimates = await navigator.storage.estimate();
            const overQuota = (estimates?.usage / estimates?.quota) * 100 >= 45;

            if (!overQuota)
              $storageService.setSessionStorage({
                key: cacheKey,
                data: response._data,
              });
          }
        },
      ];

      if (options.onResponse) {
        (Array.isArray(options.onResponse)
          ? options.onResponse
          : [options.onResponse]
        ).forEach((h) => onResponse.push(h));
      }

      if (options?.useCache) {
        const response = $storageService.getSessionStorage<T>({
          key: cacheKey,
        });
        if (!!response) {
          onResponse.forEach((onResponseFn) =>
            onResponseFn({
              request: url,
              options,
              response: { _data: response, fromCache: true },
            }),
          );
          return response;
        }
      }

      const authBases: TEndpointsWithNoTimeout[] = [
        'authApi',
        'balance',
        'player',
        'rewards',
        'casinoBonus',
      ];

      const baseKey = options?.base as TEndpointsWithNoTimeout | undefined;

      Object.assign(options, {
        baseURL,
        headers,
        query,
        ...(!baseKey ||
        !authBases.includes(baseKey) ||
        !options.useAuthentication
          ? { timeout: 15000 }
          : {}),
        ...(baseKey === 'casinoBonus' ? { timeout: 5000 } : {}),
        retry: (() => {
          if (options.retry === false || options.retry < 1) return 0;
          if (!!options.retry) return options.retry;
          return 2;
        })(),
        retryDelay: 500,
        signal: abortController.signal,
        onResponseError,
        onResponse,
      });

      return $fetch<T>(url, options);
    };

    return {
      provide: {
        fetch: api,
        abortController,
        apiCallCache,
      },
    };
  },
});
